import { useCallback, useEffect, useState } from 'react';
import { toast } from '@/components/ui/toaster.jsx';
import {
  getReviewList, getPlacingList, getFamilies, getCorrections,
  stateTypeCorrection, withdrawTypeCorrection, renameFamily, removeFamily, createFamily, correctionImpact,
} from '@/services/aircraft-identity.service.js';
import ModelSearch from '@/components/aircraft/ModelSearch.jsx';
import PlaceModel from '@/components/aircraft/PlaceModel.jsx';
import { StateTailModel, ConfirmTailModel } from '@/components/aircraft/StateTailModel.jsx';

// AIRCRAFT MODELS (MAKE-MODEL-TIME-SPEC sections 1 to 3, session 1): David's answers about what each insured aircraft
// is and which family its time counts in. Every row is a question with its answers; nothing changes until he presses one.

const btn = 'inline-flex items-center rounded-[5px] border border-accent bg-accent px-2.5 py-1 text-[12px] text-white hover:brightness-110 disabled:opacity-50';
const quiet = 'inline-flex items-center rounded-[5px] border border-border px-2.5 py-1 text-[12px] text-text hover:bg-accent-soft/40 disabled:opacity-50';
const errText = (e) => e?.body?.error || e?.message || 'failed';
const REASON_WORDS = {
  label_vs_identity: 'the label differs from its identity',
  label_vs_registry: 'the label differs from the FAA registry model',
  open: 'its model is still open',
  awaiting_confirmation: 'the applicant answered; needs your confirmation',
  no_label: 'it has no label',
};
const engineText = (r) => {
  if (!r) return 'not in the FAA registry';
  const e = r.engine;
  const eng = e ? `${e.mfr} ${e.model}${e.horsepower ? `, ${e.horsepower} hp` : ''}` : r.engine_state === 'engine_not_loaded' ? 'engine reference not loaded yet' : 'no engine on the registry';
  return `${r.model || 'no model'}${r.year ? `, ${r.year}` : ''}; engine ${eng}`;
};
const whoText = (s) => (s ? `${s.who === 'david' ? 'you' : s.who === 'applicant' ? 'the applicant' : s.by}${s.at ? `, ${String(s.at).slice(0, 10)}` : ''}` : null);

// Before a correction is written, every insured aircraft it changes is listed (David 2026-10-31); the button waits for it.
function Impact({ impact }) {
  if (!impact) return <div className="text-[12.5px] text-text-muted">Checking which insured aircraft this changes...</div>;
  if (impact.error) return <div className="text-[12.5px] text-danger">Could not check what this changes: {impact.error}</div>;
  return (
    <div className="text-[12.5px] text-text">
      <div>{impact.changes} of {impact.insured_aircraft} insured aircraft change{impact.registry_tails != null ? `; ${impact.registry_tails} registered aircraft carry this FAA model` : ''}:</div>
      <ul className="ml-4 list-disc text-text-muted">
        {impact.aircraft.map((a) => <li key={a.tail}>{a.tail}{a.year ? ` (${a.year})` : ''}: {a.changes ? `${a.before.label || 'open'} to ${a.after.label}` : `stays ${a.before.label} (stated for this aircraft)`}</li>)}
      </ul>
    </div>
  );
}

function TypeCorrection({ row, onDone }) {
  const [open, setOpen] = useState(false);
  const [pick, setPick] = useState(null);
  const [impact, setImpact] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (!pick || !row.registry?.faa_model_code) { setImpact(null); return undefined; }
    let live = true; setImpact(null);
    correctionImpact(row.registry.faa_model_code, pick.model_key).then((r) => { if (live) setImpact(r); }).catch((e) => { if (live) setImpact({ error: errText(e) }); });
    return () => { live = false; };
  }, [pick, row.registry?.faa_model_code]);
  if (!row.registry?.faa_model_code) return null;
  if (!open) return <button type="button" className={quiet} onClick={() => setOpen(true)}>Correct every {row.registry.model}</button>;
  const save = async () => {
    setSaving(true);
    try {
      const out = await stateTypeCorrection({ faa_model_code: row.registry.faa_model_code, model_key: pick.model_key, reason: `stated from the review of ${row.tail}` });
      toast.success(`Every ${out.correction.faa_name} is ${out.correction.label}`);
      onDone();
    } catch (e) { toast.error(`Not saved: ${errText(e)}`); } finally { setSaving(false); }
  };
  return (
    <div className="space-y-1.5 rounded-[5px] border border-border p-2">
      <div className="text-[12.5px] text-text-muted">Every aircraft the FAA registers as {row.registry.model} is:</div>
      {pick ? <div className="text-[13px]">{pick.label}{pick.broker ? <span className="text-text-dim"> (yours)</span> : null}</div> : <ModelSearch autoFocus onPick={setPick} />}
      {pick ? <Impact impact={impact} /> : null}
      <div className="flex gap-2">
        <button type="button" className={btn} disabled={!pick || saving || !impact || !!impact.error} onClick={save}>{saving ? 'Saving' : pick ? `Every ${row.registry.model} is ${pick.label}` : 'Pick the model'}</button>
        <button type="button" className={quiet} onClick={() => { setOpen(false); setPick(null); }}>Cancel</button>
      </div>
    </div>
  );
}

function ReviewTab({ data, reload }) {
  if (!data) return <p className="text-[13px] text-text-muted">Reading the insured aircraft...</p>;
  return (
    <div className="space-y-3">
      <p className="text-[13px] text-text-muted">{data.rows.length} of {data.aircraft} insured aircraft need a look. The registry model and engine are on each row; the label is what screens show today.</p>
      {data.rows.map((r) => (
        <div key={r.tail} className="space-y-2 rounded-card border border-border bg-surface p-3 text-[13px] text-text">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <div><span className="font-semibold">{r.tail}</span> <span className="text-text-muted">on {r.on.map((d) => d.account_name || 'a deal').join(', ')}</span></div>
            <div className="text-[12px] text-text-dim">{r.reasons.map((x) => REASON_WORDS[x] || x).join('; ')}</div>
          </div>
          <dl className="grid grid-cols-[9rem_1fr] gap-x-3 gap-y-0.5 text-[12.5px]">
            <dt className="text-text-dim">Label</dt><dd>{r.label || 'none'}{r.label_statement ? <span className="text-text-dim"> (stated by {whoText(r.label_statement)})</span> : null}</dd>
            <dt className="text-text-dim">Identity</dt><dd>{r.identity.label || 'open'} <span className="text-text-dim">({r.identity.words})</span></dd>
            <dt className="text-text-dim">FAA registry</dt><dd>{engineText(r.registry)}</dd>
          </dl>
          {r.applicant_answer ? <ConfirmTailModel tail={r.tail} answer={r.applicant_answer} onDone={reload} /> : null}
          <div className="grid gap-3 md:grid-cols-2">
            <div>
              <div className="mb-1 text-[12px] font-medium text-text-muted">This aircraft only</div>
              <StateTailModel tail={r.tail} candidates={[...(r.identity_option ? [r.identity_option] : []), ...r.candidates]} offered={r.offered_statement} onDone={reload} />
            </div>
            <div>
              <div className="mb-1 text-[12px] font-medium text-text-muted">Every aircraft of this FAA model</div>
              <TypeCorrection row={r} onDone={reload} />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function PlacingTab({ data, families, reload }) {
  if (!data) return <p className="text-[13px] text-text-muted">Reading the open deals...</p>;
  return (
    <div className="space-y-3">
      <p className="text-[13px] text-text-muted">{data.unplaced.length} insured models on open deals have no family yet ({data.placed} placed, {data.models_on_open_deals} models in all, {data.aircraft} aircraft). Each family's name words the pilot form's question.</p>
      {data.unplaced.map((m) => (
        <div key={m.model_key} className="space-y-1.5 rounded-card border border-border bg-surface p-3 text-[13px] text-text">
          <div><span className="font-semibold">{m.label}</span> <span className="text-text-muted">({m.tails.join(', ')}; {m.deal_count} deal{m.deal_count === 1 ? '' : 's'})</span></div>
          <PlaceModel model={m} families={families} onPlaced={reload} />
        </div>
      ))}
      {data.open.length ? (
        <div className="space-y-2">
          <h3 className="text-[12px] font-semibold uppercase tracking-[0.12em] text-text-muted">Model still open (placed once the data plate answers)</h3>
          {data.open.map((o) => (
            <div key={o.tail} className="space-y-1.5 rounded-card border border-border bg-surface p-3 text-[13px] text-text">
              <div><span className="font-semibold">{o.tail}</span> <span className="text-text-muted">{o.words}</span></div>
              <StateTailModel tail={o.tail} candidates={o.candidates} onDone={reload} />
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function FamilyRow({ f, reload }) {
  const [name, setName] = useState(f.display_name);
  const [arm, setArm] = useState(false);
  const rename = async () => { try { await renameFamily(f.id, name); toast.success(`Renamed to "${name.trim()}"`); reload(); } catch (e) { toast.error(`Not renamed: ${errText(e)}`); } };
  const remove = async () => { try { await removeFamily(f.id); toast.success(`"${f.display_name}" removed`); reload(); } catch (e) { toast.error(`Not removed: ${errText(e)}`); } };
  return (
    <div className="space-y-1.5 rounded-card border border-border bg-surface p-3 text-[13px] text-text">
      <div className="flex flex-wrap items-center gap-2">
        <input value={name} onChange={(e) => setName(e.target.value)} className="w-72 rounded-[5px] border border-border bg-surface px-2 py-1 text-[13px] font-medium" />
        <button type="button" className={quiet} disabled={!name.trim() || name.trim() === f.display_name} onClick={rename}>Rename to "{name.trim()}"</button>
        {f.models.length === 0 ? (arm ? <button type="button" className={btn} onClick={remove}>Yes, remove "{f.display_name}"</button> : <button type="button" className={quiet} onClick={() => setArm(true)}>Remove this empty family</button>) : null}
      </div>
      <ul className="text-[12.5px] text-text-muted">{f.models.map((m) => <li key={m.model_key}>{m.label || 'a model the loaded catalog no longer holds'}</li>)}</ul>
    </div>
  );
}

function FamiliesTab({ families, reload }) {
  const [name, setName] = useState('');
  const add = async () => { try { await createFamily(name); toast.success(`Family "${name.trim()}" named`); setName(''); reload(); } catch (e) { toast.error(`Not named: ${errText(e)}`); } };
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder='e.g. "all Cessna 172 models"' className="w-72 rounded-[5px] border border-border bg-surface px-2 py-1 text-[13px]" />
        <button type="button" className={btn} disabled={!name.trim()} onClick={add}>Name the family "{name.trim() || '...'}"</button>
      </div>
      {families.length ? families.map((f) => <FamilyRow key={f.id} f={f} reload={reload} />) : <p className="text-[13px] text-text-muted">No families yet. Name them here or from the placing list.</p>}
    </div>
  );
}

function CorrectionsTab({ corrections, reload }) {
  const [arm, setArm] = useState(null);
  const withdraw = async (c) => { try { await withdrawTypeCorrection(c.faa_model_code); toast.success(`The correction for ${c.faa_name} is withdrawn`); setArm(null); reload(); } catch (e) { toast.error(`Not withdrawn: ${errText(e)}`); } };
  if (!corrections.length) return <p className="text-[13px] text-text-muted">No type corrections yet. Write one from a review row.</p>;
  return (
    <div className="space-y-2">
      {corrections.map((c) => (
        <div key={c.faa_model_code} className="flex flex-wrap items-center justify-between gap-2 rounded-card border border-border bg-surface p-3 text-[13px] text-text">
          <div>Every <span className="font-medium">{c.faa_name}</span> is <span className="font-medium">{c.label || 'a model the loaded catalog no longer holds'}</span> <span className="text-text-dim">({c.stated_by}, {String(c.stated_at).slice(0, 10)}{c.reason ? `; ${c.reason}` : ''})</span></div>
          {arm === c.faa_model_code
            ? <button type="button" className={btn} onClick={() => withdraw(c)}>Yes, withdraw the {c.faa_name} correction</button>
            : <button type="button" className={quiet} onClick={() => setArm(c.faa_model_code)}>Withdraw</button>}
        </div>
      ))}
    </div>
  );
}

const TABS = [['review', 'Review'], ['placing', 'Placing'], ['families', 'Families'], ['corrections', 'Corrections']];

export default function AircraftModelsAdmin() {
  const [tab, setTab] = useState('review');
  const [review, setReview] = useState(null);
  const [placing, setPlacing] = useState(null);
  const [families, setFamilies] = useState([]);
  const [corrections, setCorrections] = useState([]);
  const [err, setErr] = useState(null);
  const load = useCallback(() => {
    setErr(null);
    Promise.all([getReviewList(), getPlacingList(), getFamilies(), getCorrections()])
      .then(([r, p, f, c]) => { setReview(r); setPlacing(p); setFamilies(f.families || []); setCorrections(c.corrections || []); })
      .catch((e) => setErr(errText(e)));
  }, []);
  useEffect(() => { load(); }, [load]);
  // A count is shown only once its list has been read: a zero before the read would be a placeholder spelled like an answer.
  const loaded = !!review;
  const counts = { review: review?.rows.length, placing: placing ? placing.unplaced.length + placing.open.length : undefined, families: loaded ? families.length : undefined, corrections: loaded ? corrections.length : undefined };
  return (
    <div className="mx-auto max-w-5xl space-y-4 px-4 py-6">
      <div>
        <h1 className="text-lg font-semibold text-cream">Aircraft models</h1>
        <p className="text-[13px] text-text-muted">What each insured aircraft is, and which family its time counts in. Nothing changes until you press an answer.</p>
      </div>
      {err ? <p className="text-[13px] text-danger">Could not read the aircraft identity: {err}</p> : null}
      <div className="flex flex-wrap gap-1 border-b border-border">
        {TABS.map(([k, label]) => (
          <button key={k} type="button" onClick={() => setTab(k)} className={`px-3 py-1.5 text-[13px] ${tab === k ? 'border-b-2 border-accent font-medium text-text' : 'text-text-muted'}`}>
            {label}{typeof counts[k] === 'number' ? ` (${counts[k]})` : ''}
          </button>
        ))}
      </div>
      {tab === 'review' ? <ReviewTab data={review} reload={load} /> : null}
      {tab === 'placing' ? <PlacingTab data={placing} families={families} reload={load} /> : null}
      {tab === 'families' ? <FamiliesTab families={families} reload={load} /> : null}
      {tab === 'corrections' ? <CorrectionsTab corrections={corrections} reload={load} /> : null}
    </div>
  );
}
